/**
 * Staking Rewards Chart
 *
 * Canvas-based area chart showing accumulated staking rewards over time
 * Displays total rewards earned across all staking positions
 */

import { useRef, useEffect } from 'react';
import type { StakingPosition } from '../../lib/api/cryptocom-api';
import { Icons } from '../Icons';
import { fmt } from '../../utils/formatters';

interface StakingRewardsChartProps {
  positions: StakingPosition[];
}

interface RewardsDataPoint {
  timestamp: number;
  totalRewards: number;
  pendingRewards: number;
}

export function StakingRewardsChart({ positions }: StakingRewardsChartProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  // Generate historical rewards data
  const generateRewardsData = (): RewardsDataPoint[] => {
    if (positions.length === 0) return [];

    const points: RewardsDataPoint[] = [];
    const now = Date.now();
    const daysToShow = 30;

    // Find earliest start date
    const earliestStart = Math.min(...positions.map(p => p.startDate));
    const startTime = Math.max(earliestStart, now - (daysToShow * 24 * 60 * 60 * 1000));

    // Generate daily points
    for (let i = 0; i <= daysToShow; i++) {
      const timestamp = startTime + (i * 24 * 60 * 60 * 1000);

      let totalRewards = 0;
      let pendingRewards = 0;

      positions.forEach(position => {
        if (timestamp >= position.startDate) {
          // Calculate days since position started
          const daysSinceStart = (timestamp - position.startDate) / (24 * 60 * 60 * 1000);

          // Calculate rewards earned up to this point
          // Daily reward = (amount * APR) / 365
          const dailyReward = (position.amount * position.apr / 100) / 365;
          const earnedRewards = dailyReward * daysSinceStart;

          if (timestamp < now) {
            totalRewards += earnedRewards;
          } else {
            pendingRewards += earnedRewards;
          }
        }
      });

      points.push({
        timestamp,
        totalRewards,
        pendingRewards
      });
    }

    return points;
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = rect.height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    const width = rect.width;
    const height = rect.height;

    // Clear canvas
    ctx.clearRect(0, 0, width, height);

    if (positions.length === 0) {
      ctx.fillStyle = '#888';
      ctx.font = '14px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Aucune position de staking active', width / 2, height / 2);
      return;
    }

    const rewardsData = generateRewardsData();
    if (rewardsData.length === 0) return;

    // Find min/max for scaling
    const maxRewards = Math.max(...rewardsData.map(d => d.totalRewards));
    const minTime = rewardsData[0].timestamp;
    const maxTime = rewardsData[rewardsData.length - 1].timestamp;

    // Chart margins
    const margin = { top: 20, right: 20, bottom: 40, left: 60 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    // Scale functions
    const scaleX = (timestamp: number) => {
      return margin.left + ((timestamp - minTime) / (maxTime - minTime)) * chartWidth;
    };

    const scaleY = (rewards: number) => {
      return margin.top + chartHeight - (rewards / maxRewards) * chartHeight;
    };

    // Draw grid
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.1)';
    ctx.lineWidth = 1;

    for (let i = 0; i <= 5; i++) {
      const y = margin.top + (chartHeight / 5) * i;
      ctx.beginPath();
      ctx.moveTo(margin.left, y);
      ctx.lineTo(margin.left + chartWidth, y);
      ctx.stroke();
    }

    // Draw area fill with gradient
    const gradient = ctx.createLinearGradient(0, margin.top, 0, margin.top + chartHeight);
    gradient.addColorStop(0, 'rgba(16, 185, 129, 0.3)');
    gradient.addColorStop(1, 'rgba(16, 185, 129, 0.05)');

    ctx.fillStyle = gradient;
    ctx.beginPath();

    // Start from bottom-left
    ctx.moveTo(margin.left, margin.top + chartHeight);

    // Draw area path
    rewardsData.forEach((point, i) => {
      const x = scaleX(point.timestamp);
      const y = scaleY(point.totalRewards);

      if (i === 0) {
        ctx.lineTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });

    // Close path to bottom-right
    const lastX = scaleX(rewardsData[rewardsData.length - 1].timestamp);
    ctx.lineTo(lastX, margin.top + chartHeight);
    ctx.closePath();
    ctx.fill();

    // Draw line
    ctx.strokeStyle = '#10b981';
    ctx.lineWidth = 2;
    ctx.beginPath();

    rewardsData.forEach((point, i) => {
      const x = scaleX(point.timestamp);
      const y = scaleY(point.totalRewards);

      if (i === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    });

    ctx.stroke();

    // Draw current value dot
    const lastPoint = rewardsData[rewardsData.length - 1];
    const lastPointX = scaleX(lastPoint.timestamp);
    const lastPointY = scaleY(lastPoint.totalRewards);

    ctx.fillStyle = '#10b981';
    ctx.beginPath();
    ctx.arc(lastPointX, lastPointY, 5, 0, Math.PI * 2);
    ctx.fill();

    // Draw Y-axis labels
    ctx.fillStyle = '#888';
    ctx.font = '11px sans-serif';
    ctx.textAlign = 'right';

    for (let i = 0; i <= 5; i++) {
      const rewards = (maxRewards / 5) * i;
      const y = margin.top + chartHeight - (chartHeight / 5) * i;
      ctx.fillText(`${rewards.toFixed(0)}`, margin.left - 10, y + 4);
    }

    // Draw X-axis labels
    ctx.textAlign = 'center';
    const numXLabels = 5;
    for (let i = 0; i <= numXLabels; i++) {
      const timestamp = minTime + ((maxTime - minTime) / numXLabels) * i;
      const x = scaleX(timestamp);
      const date = new Date(timestamp);
      const label = `${date.getMonth() + 1}/${date.getDate()}`;
      ctx.fillText(label, x, height - 15);
    }

  }, [positions]);

  const totalRewards = positions.reduce((sum, p) => sum + p.totalRewards, 0);
  const pendingRewards = positions.reduce((sum, p) => sum + p.pendingRewards, 0);
  const totalStaked = positions.reduce((sum, p) => sum + p.amount, 0);
  const avgAPR = positions.length > 0
    ? positions.reduce((sum, p) => sum + p.apr, 0) / positions.length
    : 0;

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <Icons.DollarSign size={20} />
          Rewards de Staking (30 jours)
        </h3>
        <div style={{ display: 'flex', gap: '16px', fontSize: '14px' }}>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: '#888', fontSize: '12px' }}>Total Rewards</div>
            <div style={{ color: '#10b981', fontWeight: 600 }}>{fmt.currency(totalRewards)}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ color: '#888', fontSize: '12px' }}>En attente</div>
            <div style={{ color: '#f59e0b', fontWeight: 600 }}>{fmt.currency(pendingRewards)}</div>
          </div>
        </div>
      </div>

      <canvas
        ref={canvasRef}
        style={{
          width: '100%',
          height: '250px',
          cursor: 'default'
        }}
      />

      {/* Stats summary */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
        gap: '16px',
        marginTop: '16px',
        padding: '16px',
        backgroundColor: 'rgba(255,255,255,0.03)',
        borderRadius: '8px'
      }}>
        <div>
          <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>Total Staké</div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#fff' }}>
            {fmt.currency(totalStaked)}
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>APR Moyen</div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#10b981' }}>
            {avgAPR.toFixed(2)}%
          </div>
        </div>
        <div>
          <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>Positions</div>
          <div style={{ fontSize: '16px', fontWeight: 600, color: '#fff' }}>
            {positions.length}
          </div>
        </div>
      </div>
    </div>
  );
}
